import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import {
  appendTimeSeriesTooltipOverlay,
  buildTooltipHtml,
  createTooltipHandlers,
  dashboardSeriesColors,
  formatTooltipCurrency,
  formatTooltipDate,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";
import Loading from "../../common/components/loading/loading";

const DailyPoolPriceLineGraph = ({ data, isLoading }) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    if (!svg || !svg.node()) return;
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    // Calculate dimensions based on the size of the parent container
    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight;
    const margin = { top: 40, right: 10, bottom: 40, left: 60 };

    // Set dimensions considering margins
    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    // Create SVG
    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none") // Preserve aspect ratio
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    // // Parse dates and calculate monthly averages
    // const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%S");

    const monthlyData = d3
      .rollups(
        data,
        (v) => ({
          monthStart: d3.timeMonth.floor(new Date(v[0].marketDate)),
          avgPoolPrice: d3.mean(v, (d) => d.poolPrice),
        }),
        (d) => d3.timeMonth.floor(new Date(d.marketDate)),
      )
      .map(([month, values]) => ({
        date: values.monthStart,
        poolPrice: values.avgPoolPrice,
      }));

    // Group data by year
    const groupedData = d3.group(monthlyData, (d) => d.date.getFullYear());

    // Prepare data for plotting
    const plotData = Array.from(groupedData, ([year, values]) => {
      return {
        year: year,
        values: values.map((d) => ({
          date: new Date(2000, d.date.getMonth(), 1),
          poolPrice: d.poolPrice,
        })),
      };
    });

    // Set up scales
    const xScale = d3
      .scaleTime()
      .domain([new Date(2000, 0, 1), new Date(2000, 11, 31)]) // Full year range
      .range([0, innerWidth]);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(monthlyData, (d) => d.poolPrice)])
      .nice()
      .range([innerHeight, 0]);

    // Add gridlines
    chartSvg
      .append("g")
      .attr("class", "grid  d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    // Color scale for different years
    const colorScale = d3.scaleOrdinal(d3.schemeCategory10);
    const highlightFillColor = dashboardSeriesColors.accentRed;
    const highlightLineColor = "red";
    const highlightGradientId = `daily-pool-price-area-${Math.random().toString(36).slice(2)}`;

    const gradient = chartSvg
      .append("defs")
      .append("linearGradient")
      .attr("id", highlightGradientId)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", highlightFillColor)
      .attr("stop-opacity", 0.24);

    gradient
      .append("stop")
      .attr("offset", "38%")
      .attr("stop-color", highlightFillColor)
      .attr("stop-opacity", 0.09);

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", highlightFillColor)
      .attr("stop-opacity", 0);

    // Create line generator
    const line = d3
      .line()
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.poolPrice))
      .defined((d) => !isNaN(d.poolPrice))
      .curve(d3.curveMonotoneX); // Handle any potential NaN values

    const area = d3
      .area()
      .x((d) => xScale(d.date))
      .y0(innerHeight)
      .y1((d) => yScale(d.poolPrice))
      .defined((d) => !isNaN(d.poolPrice))
      .curve(d3.curveMonotoneX);

    // Find the most recent year
    const mostRecentYear = d3.max(plotData, (d) => d.year);
    const highlightedSeries = plotData.find(
      (yearData) => yearData.year === mostRecentYear,
    );
    const comparisonSeries = plotData.filter(
      (yearData) => yearData.year !== mostRecentYear,
    );

    const areaLayer = chartSvg.append("g").attr("class", "dashboard-area-layer");
    const lineLayer = chartSvg.append("g").attr("class", "dashboard-line-layer");

    if (highlightedSeries) {
      areaLayer
        .append("path")
        .datum(highlightedSeries.values)
        .attr("class", "d3-chart-area dashboard-area-highlight")
        .attr("fill", `url(#${highlightGradientId})`)
        .attr("d", area);
    }

    comparisonSeries.forEach((yearData) => {
      const path = line(yearData.values);

      lineLayer
        .append("path")
        .datum(yearData.values)
        .attr(
          "class",
          "line dashboard-series-comparison dashboard-series-dashed",
        )
        .attr("fill", "none")
        .attr("stroke", colorScale(yearData.year))
        .attr("stroke-width", 1.2)
        .attr("d", path)
        .attr("stroke-dasharray", "2 2");
    });

    if (highlightedSeries) {
      lineLayer
        .append("path")
        .datum(highlightedSeries.values)
        .attr("class", "line dashboard-series-primary")
        .attr("fill", "none")
        .attr("stroke", highlightLineColor)
        .attr("stroke-width", 2.75)
        .attr("d", line(highlightedSeries.values))
        .attr("stroke-dasharray", "0 0");
    }

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    appendTimeSeriesTooltipOverlay({
      chartSvg,
      innerWidth,
      innerHeight,
      xScale,
      getData: () => Array.from(new Set(plotData.flatMap((seriesData) => seriesData.values.map((value) => value.date.getMonth())))).sort((a, b) => a - b).map((month) => ({ date: new Date(2000, month, 1) })),
      xAccessor: (d) => d.date,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) =>
        buildTooltipHtml({
          title: formatTooltipDate(d.date, "%b"),
          rows: plotData.map((seriesData) => {
            const point = seriesData.values.find(
              (value) => value.date.getMonth() === d.date.getMonth(),
            );
            return {
              label: String(seriesData.year),
              value: formatTooltipCurrency(point?.poolPrice, 0),
              color:
                seriesData.year === mostRecentYear
                  ? highlightLineColor
                  : colorScale(seriesData.year),
            };
          }),
        }),
    });

    // Add X axis
    chartSvg
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(d3.timeMonth.every(1))
          .tickFormat(d3.timeFormat("%b")),
      )
      .attr("class", "d3-chart-axis");

    // Format for currency with 2 decimal places
    const formatCurrency = d3.format("$,.0f");

    // Add Y axis
    chartSvg
      .append("g")
      .call(d3.axisLeft(yScale).tickFormat(formatCurrency))
      .attr("class", "d3-chart-axis");

    // Add Y axis label
    chartSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .attr("font-size", "14px")
      .text("Average Pool Price ($/MWh)")
      .attr("class", "d3-chart-axis-label");

    // Add title
    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text("AESO Historical Monthly Average Pool Price")
      .attr("class", "d3-chart-title");

    // Add legend at the bottom
    const legendHeight = 20;
    const legendLineWidth = 20;
    const legendItemWidth = 70;

    svg.attr("height", parentHeight + legendHeight + 10);

    const totalLegendWidth = plotData.length * legendItemWidth;

    const legend = chartSvg
      .append("g")
      .attr("font-family", "sans-serif")
      .attr("font-size", 12)
      .attr("class", "d3-chart-legend-label")
      .attr("text-anchor", "start")
      .attr(
        "transform",
        `translate(${(innerWidth - totalLegendWidth) / 2}, ${
          innerHeight + margin.bottom - legendHeight
        })`,
      );

    plotData.forEach((yearData, i) => {
      const legendItem = legend
        .append("g")
        .attr("transform", `translate(${i * legendItemWidth}, 0)`);

      legendItem
        .append("line")
        .attr("x1", 0)
        .attr("y1", legendHeight / 2)
        .attr("x2", legendLineWidth)
        .attr("y2", legendHeight / 2)
        .attr(
          "stroke",
          yearData.year === mostRecentYear ? "red" : colorScale(yearData.year),
        )
        .attr("stroke-width", yearData.year === mostRecentYear ? 2 : 1)
        .attr(
          "stroke-dasharray",
          yearData.year === mostRecentYear ? "0 0" : "2 2",
        );

      legendItem
        .append("text")
        .attr("x", legendLineWidth + 5)
        .attr("y", legendHeight / 2)
        .attr("dy", "0.35em")
        .text(yearData.year);
    });
  }, [data]);

  useEffect(() => {
    !isLoading && renderChart();
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current; // <- capture current value
    if (!svgNode) return;

    const throttledResize = (entries) => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100); // throttle duration in ms
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;
  return <svg ref={svgRef} className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}></svg>;
};

export default DailyPoolPriceLineGraph;


















